import { NgModule } from "@angular/core";
import { BrowserModule, Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { CommonModule, DatePipe } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { RouterModule } from "@angular/router";

// ng-datatable
import { DataTableModule } from "@bhplugin/ng-datatable";

// Routes
import { routes } from "./app.route";

import { AppComponent } from "./app.component";

// service
import { AppService } from "./shared/services/app.service";

// i18n
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
// select
import { NgItemLabelDirective, NgSelectModule } from "@ng-select/ng-select";

// headlessui
import { MenuModule } from "headlessui-angular";

// perfect-scrollbar
import { NgScrollbarModule } from "ngx-scrollbar";

// dashboard
import { IndexComponent } from "./index";

// tippy
import { NgxTippyModule } from "ngx-tippy-wrapper";

// store
import { StoreModule } from "@ngrx/store";

// quill editor
import { QuillModule } from "ngx-quill";
// Layouts
import { AppLayout } from "./layouts/app-layout";
import { AuthLayout } from "./layouts/auth-layout";

// flatpicker

import { HeaderComponent } from "./layouts/header";
import { FooterComponent } from "./layouts/footer";
import { SidebarComponent } from "./layouts/sidebar";
import { ThemeCustomizerComponent } from "./layouts/theme-customizer";
import { IconModule } from "./shared/icon/icon.module";
import { DashboardComponent } from "./components/dashboard/dashboard.component";

// apexchart
import { NgApexchartsModule } from "ng-apexcharts";
import { CustomersTableComponent } from "./components/customer/customers-table/customer-page.component";
import { StatisticsComponent } from "./components/statistics/statistics.component";
import { TierManagmentComponent } from "./components/tier-managment/tier-managment.component";
import { AdminTableComponent } from "./components/admin-table/admin-table.component";
import { BlogsComponent } from "./components/blogs/blogs.component";
import { HIGHLIGHT_OPTIONS, HighlightModule } from "ngx-highlightjs";
import { UserSettingsComponent } from "./components/user-profile/user-profile.component";
import { JwtInterceptor } from "src/app/shared/interceptors/jwt.interceptor";
import { authReducer } from "./store/auth/auth.reducer";
import { indexReducer } from "./store/index.reducer";
import { dateTimeReducer } from "./store/datepicker/datepicker.reducer";
import { AuthService } from "./auth/service/auth.service";
import { HasRoleGuard } from "./shared/guards/has-role.guard";
import { LoginComponent } from "./auth/login";
import { ContractTableComponent } from "./components/contract/contract-table/contract-table.component";

import { PicklistSelectComponent } from "./components/picklist/picklist-select/picklist-select.component";
import { ConfigComponent } from "./components/config/config.component";
import { CustomerFormComponent } from "./components/customer/customer-form/customer-form.component";
import { ContractDetailsComponent } from "./components/contract/contract-details/contract-details.component";

// File upload
import { BreadcrumbsComponent } from "./shared/components/breadcrumbs/breadcrumbs";
import { ContractEditComponent } from "./components/contract/contract-edit/contract-edit.component";

import { ContractBillsComponent } from "./components/bill/contract-bills/contract-bills.component";
import { FlatpickrModule } from "angularx-flatpickr";
import { BillViewComponent } from "./components/bill/bill-view/bill-view.component";
import { BillingComponent } from "./components/bill/billing-page.component";
import { UserNewPasswordComponent } from "./components/user-profile/user-profile-new-password/user-profile-new-password.component";
import { AgencyProfileComponent } from "./components/user-profile/agency-profile/agency-profile.component";
import { PickListComponent } from "./components/picklist/picklist-page.component";
import { IconEyeClosedComponent } from "./shared/icon/icon-eye-closed";
import { IconEyeOpenComponent } from "./shared/icon/icon-eye-open";
import { ContractDomainComponent } from "./components/contract/contract-domain/contract-domain.component";
import { ContractFormComponent } from "./components/contract/contract-form/contract-form.component";
import { PicklistFormComponent } from "./components/picklist/picklist-form/picklist-form.component";
import { Error404Component } from "./shared/components/error404/error404";
import { InstanceComponent } from "./components/instance/instance.component";
import { BlogFormComponent } from "./components/blogs/blog-form/blog-form.component";
import { AnalyticsComponent } from "./components/analytics/analytics.component";
import { provideDialogConfig } from "@ngneat/dialog";
import { UsersTableComponent } from "./components/users/users.table/users.table.component";
import { MessagePortalComponent } from "./portal/portal";
import { PortalModule } from "@angular/cdk/portal";
import { UserFormComponent } from "./components/users/user-form/user-form.component";
import { DirectivesModule } from "./directives/directives.module";
import { CustomerDetailsComponent } from "./components/customer/customer-details/customer-details.component";
import { ContractUsersTableComponent } from "./components/users/contract-users.table/contract-users.table.component";
import { QnAListComponent } from "./components/qna/qna-list/qna-list.component";
import { SortablejsModule } from "@dustfoundation/ngx-sortablejs";
import { QnAFormComponent } from "./components/qna/qna-form/qna-form.component";
import { TagInputModule } from "ngx-chips";
import { SearchInputComponent } from "./shared/components/search-input/search-input.component";
import { CurrencySymbolPipe } from "./shared/pipes/currency-symbol-pipe";
import { InstanceRequestsTable } from "./components/instance-requests/instance-requests.table/instance-requests.table.component";
import { GlobalTableFiltersComponent } from "./shared/components/global-table-filters/global-table-filters.component";
import { ContractDbImportComponent } from "./components/contract/contract-db-import/contract-db-import.component";
import { TestemonialsTableComponent } from "./components/testemonials/testemonials.table/testemonials.table.component";
import { TestemonailFormComponent } from "./components/testemonials/testemonials.form/testemonials.form.component";
import { PicklistOptionComponent } from "./components/picklist/picklist-option/picklist-option.component";
import { TaxRateCategories } from "./components/user-profile/tax-rates.configuration/tax-rates.configuration.component";
import { TaxRatesModalComponent } from "./components/user-profile/tax-rates.configuration/tax-rates.modal/tax-rates.modal.component";
import { EInvoiceComponent } from "./components/e-invoice/e-invoice/e-invoice.component";
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from "ngx-mask";
import { LoaderComponent } from "./shared/components/loader/loader.component";
import { EInvoiceActionsComponent } from "./components/e-invoice/e-invoice-actions/e-invoice-actions.component";
import { SafePipe } from "./shared/pipes/safe";
import { EInvoicePdfViewerComponent } from "./components/e-invoice/e-invoice-pdf-viewer/e-invoice-pdf-viewer.component";
import { EInvoiceStornoComponent } from "./components/e-invoice/e-invoice-storno/e-invoice-storno.component";
import { ConfigurationEInvoiceComponent } from "./components/e-invoice/configuration-e-invoice/configuration-e-invoice.component";

@NgModule({
  declarations: [
    ConfigurationEInvoiceComponent,
    EInvoiceStornoComponent,
    EInvoicePdfViewerComponent,
    SafePipe,
    EInvoiceActionsComponent,
    LoaderComponent,
    EInvoiceComponent,
    PicklistOptionComponent,
    TaxRateCategories,
    TaxRatesModalComponent,
    TestemonailFormComponent,
    TestemonialsTableComponent,
    ContractDbImportComponent,
    GlobalTableFiltersComponent,
    InstanceRequestsTable,
    CurrencySymbolPipe,
    SearchInputComponent,
    QnAListComponent,
    QnAFormComponent,
    ContractUsersTableComponent,
    CustomerDetailsComponent,
    UserFormComponent,
    MessagePortalComponent,
    UsersTableComponent,
    InstanceComponent,
    BreadcrumbsComponent,
    ContractTableComponent,
    ContractDomainComponent,
    ContractFormComponent,
    AppComponent,
    UserNewPasswordComponent,
    AgencyProfileComponent,
    PicklistFormComponent,
    PickListComponent,
    IconEyeClosedComponent,
    IconEyeOpenComponent,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    ThemeCustomizerComponent,
    IndexComponent,
    AppLayout,
    AuthLayout,
    LoginComponent,
    Error404Component,
    DashboardComponent,
    BillingComponent,
    CustomersTableComponent,
    StatisticsComponent,
    TierManagmentComponent,
    AdminTableComponent,
    BlogsComponent,
    UserSettingsComponent,
    BlogFormComponent,
    PicklistSelectComponent,
    ContractTableComponent,
    ContractEditComponent,
    ConfigComponent,
    CustomerFormComponent,
    ContractDetailsComponent,
    ContractBillsComponent,
    BillViewComponent,
    AnalyticsComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    NgxMaskDirective,
    NgxMaskPipe,
    TagInputModule,
    SortablejsModule.forRoot({ animation: 150 }),
    DirectivesModule,
    NgApexchartsModule,
    PortalModule,
    RouterModule.forRoot(routes, { scrollPositionRestoration: "enabled" }),
    BrowserModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    NgxTippyModule,
    FormsModule,
    ReactiveFormsModule,
    HighlightModule,
    MenuModule,
    DataTableModule,
    FlatpickrModule,
    FlatpickrModule.forRoot(),
    NgSelectModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoader,
        deps: [HttpClient],
      },
    }),
    StoreModule.forRoot({
      datetime: dateTimeReducer,
      auth: authReducer,
      index: indexReducer,
    }),
    NgScrollbarModule,
    QuillModule.forRoot(),
    IconModule,
    NgItemLabelDirective,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    AppService,
    Title,
    HasRoleGuard,
    AuthService,
    DatePipe,
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import("highlight.js/lib/core"),
        languages: {
          json: () => import("highlight.js/lib/languages/json"),
          typescript: () => import("highlight.js/lib/languages/typescript"),
          xml: () => import("highlight.js/lib/languages/xml"),
        },
      },
    },
    provideNgxMask(),
    provideDialogConfig({
      enableClose: { escape: true, backdrop: false },
    }),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}

// AOT compilation support
export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
